/**
 * Shared, interactive forecast charts.
 *
 * One source of truth for the Forecast Studio AND the Inference detail page so
 * every chart in the product looks and behaves identically:
 *   * FanChart  — history + point path + quantile band (forecast_value /
 *                 forecast_distribution).
 *   * ScoreBars — per-step up/anomaly likelihood with a 50% reference line
 *                 (trend / anomaly).
 *   * ForecastValuesTable — the numbers behind whichever chart is shown.
 *
 * Interactions (uniform across both charts):
 *   * Hover crosshair + cursor-following tooltip.
 *   * Zoom: drag-select a range, scroll-wheel to zoom around the cursor, a
 *     "Reset zoom" affordance, and double-click to reset. Y auto-fits the
 *     visible window so a zoom actually reveals detail.
 */
import React, {
  useEffect, useMemo, useRef, useState, useCallback,
} from 'react';
import { format } from 'date-fns';
import {
  Box, Stack, Typography, Button, Table, TableBody, TableCell, TableHead,
  TableRow, IconButton, Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Forecast accent — theme-aware so the line reads well on either background:
// a bright lime on dark, a deep forest/teal green on light. Exported so every
// forecast surface (incl. the row sparkline) shares one accent.
export const accentFor = (mode) => (mode === 'dark' ? '#a3e635' : '#2f8163');

// Compact form for cramped axis ticks (no year — the axis spans a single
// visible range, so the year would just add clutter).
export const fmtChartTs = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'MMM d, HH:mm'); } catch (_e) { return '—'; }
};

// Full form (with year) for the values table and hover tooltips, where the
// reader needs the absolute date — forecasts routinely cross a year boundary
// and historical series can sit decades back, so an unqualified "Jul 19" is
// ambiguous. Drops the time for day-or-coarser cadences (where HH:mm is just
// a bucket-interval artifact, e.g. monthly buckets landing at 04:09).
export const fmtChartTsFull = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'MMM d, yyyy, HH:mm'); } catch (_e) { return '—'; }
};

export const fmtChartNum = (v) => {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const a = Math.abs(n);
  // Thousands separators + sensible precision per magnitude
  // (e.g. 64560 -> "64,560", 12.34, 0.0123).
  const maxDigits = a >= 1000 ? 0 : a >= 10 ? 2 : 4;
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDigits,
  });
};

export const useChartWidth = (initial = 720) => {
  const ref = useRef(null);
  const [width, setWidth] = useState(initial);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
};

// Cursor-following tooltip, clamped to the chart box so it never clips.
export const ChartTooltip = ({ x, y, boxW, children }) => {
  const TW = 178;
  const left = Math.min(Math.max(x + 14, 6), Math.max(boxW - TW - 6, 6));
  const top = Math.max(y - 12, 6);
  return (
    <Box
      sx={{
        position: 'absolute', left, top, width: TW, pointerEvents: 'none',
        zIndex: 6, bgcolor: 'background.paper', border: '1px solid',
        borderColor: 'divider', borderRadius: 1.5, px: 1.25, py: 0.75,
        boxShadow: 6,
      }}
    >
      {children}
    </Box>
  );
};

// Zoom over an integer index domain [0, n-1]. Returns the visible [i0, i1]
// plus imperative zoom helpers shared by both charts.
const useIndexZoom = (n) => {
  const [view, setView] = useState(null); // [i0, i1] | null (= full range)
  useEffect(() => { setView(null); }, [n]); // reset when the series changes
  const reset = useCallback(() => setView(null), []);
  const i0 = view ? view[0] : 0;
  const i1 = view ? view[1] : Math.max(n - 1, 0);

  const zoomTo = useCallback((a, b) => {
    const lo = Math.max(0, Math.min(a, b));
    const hi = Math.min(n - 1, Math.max(a, b));
    if (hi - lo < 1) return;
    if (lo <= 0 && hi >= n - 1) { setView(null); return; }
    setView([lo, hi]);
  }, [n]);

  const zoomBy = useCallback((factor, centerFrac) => {
    setView((prev) => {
      const curLo = prev ? prev[0] : 0;
      const curHi = prev ? prev[1] : n - 1;
      const span = curHi - curLo;
      const center = curLo + centerFrac * span;
      const newSpan = Math.max(2, Math.min(n - 1, span * factor));
      let lo = Math.round(center - newSpan * centerFrac);
      let hi = Math.round(lo + newSpan);
      if (lo < 0) { hi -= lo; lo = 0; }
      if (hi > n - 1) { lo -= (hi - (n - 1)); hi = n - 1; }
      lo = Math.max(0, lo);
      if (hi - lo >= n - 1) return null;
      if (hi - lo < 1) return prev;
      return [lo, hi];
    });
  }, [n]);

  return {
    i0, i1, zoomed: !!view, reset, zoomTo, zoomBy,
  };
};

// Floating chart toolbar (magnifying-glass zoom in/out + reset), top-right.
const ChartTools = ({
  onZoomIn, onZoomOut, onReset, zoomed,
}) => (
  <Stack
    direction="row" spacing={0.25}
    sx={{
      position: 'absolute', top: 6, right: 6, zIndex: 7,
      bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
      borderRadius: 1, p: 0.25, boxShadow: 2,
    }}
  >
    <Tooltip title="Zoom in">
      <IconButton size="small" onClick={onZoomIn} sx={{ p: 0.4 }}>
        <ZoomInIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Zoom out">
      <IconButton size="small" onClick={onZoomOut} sx={{ p: 0.4 }}>
        <ZoomOutIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Reset zoom">
      <span>
        <IconButton size="small" onClick={onReset} disabled={!zoomed} sx={{ p: 0.4 }}>
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  </Stack>
);

// Native, non-passive wheel-zoom binding (React's onWheel is passive, so
// preventDefault wouldn't stop the page scrolling). Reads geometry from a ref
// so the handler never goes stale across re-renders.
const useWheelZoom = (svgRef, stateRef) => {
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const s = stateRef.current;
      if (!s || !s.innerW) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const frac = Math.max(0, Math.min(1,
        (e.clientX - rect.left - s.left) / Math.max(s.innerW, 1)));
      s.zoomBy(e.deltaY > 0 ? 1.25 : 0.8, frac);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [svgRef, stateRef]);
};

/* ------------------------------------------------------------------ */
/* FanChart                                                            */
/* ------------------------------------------------------------------ */

export const FanChart = ({ forecast, events = null, height = 300 }) => {
  const theme = useTheme();
  const ACCENT = accentFor(theme.palette.mode);
  const EVENT_COLOR = theme.palette.warning.main;
  // The target's measurement unit ("$/MWh"), stamped on the forecast
  // payload by the backend when the signal catalog knows it.
  const unit = forecast?.unit || null;
  const fmtVal = (v) => (unit ? `${fmtChartNum(v)} ${unit}` : fmtChartNum(v));
  const [containerRef, width] = useChartWidth();
  const svgRef = useRef(null);
  const stateRef = useRef({});
  const [hoverI, setHoverI] = useState(null);
  const [hoverEvent, setHoverEvent] = useState(null); // marker under cursor
  const [drag, setDrag] = useState(null); // {a, b} in px while selecting

  const model = useMemo(() => {
    if (!forecast?.point?.length) return null;
    const histVals = (forecast.history?.values || []).map(
      (v) => (v == null ? null : Number(v)),
    );
    const maxHist = Math.max(24, forecast.point.length * 3);
    const hist = histVals.slice(-maxHist);
    const histAt = (forecast.history?.step_at || []).slice(-maxHist);
    const qs = Object.keys(forecast.quantiles || {}).sort(
      (a, b) => Number(a) - Number(b),
    );
    const lo = qs.length >= 2 ? forecast.quantiles[qs[0]] : null;
    const hi = qs.length >= 2 ? forecast.quantiles[qs[qs.length - 1]] : null;
    const f0 = hist.length;
    const n = hist.length + forecast.point.length;
    const times = []; const vals = [];
    for (let i = 0; i < n; i += 1) {
      if (i < f0) { times.push(histAt[i]); vals.push(hist[i]); } else {
        times.push(forecast.step_at?.[i - f0]);
        vals.push(Number(forecast.point[i - f0]));
      }
    }
    return {
      hist, lo, hi, f0, n, times, vals,
      qLabels: qs.length >= 2 ? [qs[0], qs[qs.length - 1]] : null,
    };
  }, [forecast]);

  // Event markers: map each event's occurred_at onto the chart's index
  // axis (fractional, interpolated between neighboring buckets). Past
  // events = things that happened over the shown history; scheduled =
  // known-future occurrences inside the horizon, which the engine already
  // overlays onto the forecast when the stream has a validated edge.
  const eventMarks = useMemo(() => {
    if (!model) return [];
    const list = [
      ...((events?.past || []).map((e) => ({ ...e, scheduled: false }))),
      ...((events?.scheduled || []).map((e) => ({ ...e, scheduled: true }))),
    ];
    if (!list.length) return [];
    const tms = model.times.map((t) => (t ? new Date(t).getTime() : NaN));
    const firstMs = tms.find((v) => Number.isFinite(v));
    const lastMs = [...tms].reverse().find((v) => Number.isFinite(v));
    if (!Number.isFinite(firstMs) || !Number.isFinite(lastMs)) return [];
    const marks = [];
    list.forEach((e) => {
      const ms = Number(e.occurred_at);
      if (!Number.isFinite(ms) || ms < firstMs || ms > lastMs) return;
      let idx = model.n - 1;
      for (let j = 0; j < tms.length; j += 1) {
        if (!Number.isFinite(tms[j])) continue;
        if (tms[j] >= ms) {
          if (j === 0) { idx = 0; break; }
          const prev = tms[j - 1];
          idx = Number.isFinite(prev) && tms[j] > prev
            ? (j - 1) + (ms - prev) / (tms[j] - prev)
            : j;
          break;
        }
      }
      marks.push({ ...e, idx });
    });
    // Stack markers that land on the same bucket so none are hidden.
    const byBucket = {};
    marks.sort((a, b) => a.idx - b.idx).forEach((m) => {
      const b = Math.round(m.idx);
      byBucket[b] = (byBucket[b] || 0) + 1;
      m.stack = byBucket[b] - 1;
    });
    return marks;
  }, [model, events]);

  const n = model?.n || 0;
  const {
    i0, i1, zoomed, reset, zoomTo, zoomBy,
  } = useIndexZoom(n);
  useWheelZoom(svgRef, stateRef);

  const M = { top: 14, right: 14, bottom: 28, left: 58 };
  const W = Math.max(width, 320);
  const innerW = W - M.left - M.right;
  const innerH = height - M.top - M.bottom;
  stateRef.current = { left: M.left, innerW, zoomBy };

  if (!model) return null;

  // Y auto-fit to the visible window (so zooming reveals detail).
  let yMin = Infinity; let yMax = -Infinity;
  for (let i = i0; i <= i1; i += 1) {
    const v = model.vals[i];
    if (v != null && Number.isFinite(v)) { yMin = Math.min(yMin, v); yMax = Math.max(yMax, v); }
    if (i >= model.f0 && model.lo) {
      const l = Number(model.lo[i - model.f0]);
      const h = Number(model.hi[i - model.f0]);
      if (Number.isFinite(l)) yMin = Math.min(yMin, l);
      if (Number.isFinite(h)) yMax = Math.max(yMax, h);
    }
  }
  if (!Number.isFinite(yMin) || !Number.isFinite(yMax)) { yMin = 0; yMax = 1; }
  if (yMin === yMax) { yMin -= 1; yMax += 1; }
  const padY = (yMax - yMin) * 0.1; yMin -= padY; yMax += padY;

  const span = Math.max(i1 - i0, 1);
  const x = (i) => M.left + (innerW * (i - i0)) / span;
  const y = (v) => M.top + innerH * (1 - (v - yMin) / (yMax - yMin));
  const pxToIndex = (px) => Math.max(i0, Math.min(i1,
    Math.round(i0 + ((px - M.left) / Math.max(innerW, 1)) * span)));

  // Visible history line.
  const histVis = [];
  for (let i = i0; i <= Math.min(i1, model.f0 - 1); i += 1) {
    if (model.vals[i] != null) histVis.push([i, model.vals[i]]);
  }
  const histPath = histVis
    .map(([i, v], k) => `${k === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ');

  // Visible forecast line (prepend last hist point for continuity).
  const fcVis = [];
  const lastHistIdx = model.f0 - 1;
  if (lastHistIdx >= i0 && lastHistIdx <= i1 && model.vals[lastHistIdx] != null) {
    fcVis.push([lastHistIdx, model.vals[lastHistIdx]]);
  }
  for (let i = Math.max(i0, model.f0); i <= i1; i += 1) fcVis.push([i, model.vals[i]]);
  const fcPath = fcVis
    .map(([i, v], k) => `${k === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ');
  const baseY = (height - M.bottom).toFixed(1);
  const fcArea = fcVis.length > 1
    ? `${fcPath} L${x(fcVis[fcVis.length - 1][0]).toFixed(1)},${baseY} L${x(fcVis[0][0]).toFixed(1)},${baseY} Z`
    : null;

  // Visible band.
  let bandPath = null;
  if (model.lo && model.hi) {
    const fIdx = [];
    for (let i = Math.max(i0, model.f0); i <= i1; i += 1) fIdx.push(i);
    if (fIdx.length > 1) {
      const up = fIdx.map((i) => `${x(i).toFixed(1)},${y(Number(model.hi[i - model.f0])).toFixed(1)}`);
      const dn = fIdx.map((i) => `${x(i).toFixed(1)},${y(Number(model.lo[i - model.f0])).toFixed(1)}`).reverse();
      bandPath = `M${up[0]} ${up.slice(1).map((p) => `L${p}`).join(' ')} ${dn.map((p) => `L${p}`).join(' ')} Z`;
    }
  }

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => yMin + t * (yMax - yMin));
  const nTicks = Math.min(6, span + 1);
  const xTickIdx = Array.from({ length: nTicks }, (_, k) => Math.round(
    i0 + (k * span) / Math.max(nTicks - 1, 1),
  ));
  const nowVisible = lastHistIdx >= i0 && lastHistIdx <= i1;
  // The divider marks the forecast ORIGIN — the last realized data point —
  // which is only "now" for live data. For a historical series (e.g. ending
  // 1960) the origin sits decades back, so labeling it "now" is wrong. Call
  // it "now" only when the origin is within ~one bucket of the present;
  // otherwise show its real date so the label is always truthful.
  // The forecast origin = the last REALIZED history point. Scan backward for
  // the last non-null timestamp so a padded/missing tail bucket doesn't leave
  // the divider mislabeled as "now" (mirrors predictionToForecast's anchor).
  const originTs = (() => {
    for (let i = lastHistIdx; i >= 0; i -= 1) {
      if (model.times[i]) return model.times[i];
    }
    return null;
  })();
  const dividerLabel = (() => {
    if (!originTs) return 'now';
    const oMs = new Date(originTs).getTime();
    if (!Number.isFinite(oMs)) return 'now';
    const nextTs = model.times[model.f0];
    const oNext = nextTs ? new Date(nextTs).getTime() : NaN;
    const stepMs = Number.isFinite(oNext) && oNext > oMs ? oNext - oMs : 0;
    const tol = Math.max(stepMs * 1.5, 6 * 60 * 60 * 1000); // ≥ 6h slack
    if (Math.abs(Date.now() - oMs) <= tol) return 'now';
    try { return format(new Date(originTs), 'MMM d, yyyy'); } catch (_e) { return fmtChartTs(originTs); }
  })();

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    if (drag) { setDrag({ a: drag.a, b: px }); setHoverI(null); return; }
    setHoverI(pxToIndex(px));
  };
  const onDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDrag({ a: e.clientX - rect.left, b: e.clientX - rect.left });
  };
  const onUp = () => {
    if (drag && Math.abs(drag.b - drag.a) > 6) zoomTo(pxToIndex(drag.a), pxToIndex(drag.b));
    setDrag(null);
  };

  const hv = hoverI != null ? model.vals[hoverI] : null;
  const inFc = hoverI != null && hoverI >= model.f0;
  const bandLo = inFc && model.lo ? Number(model.lo[hoverI - model.f0]) : null;
  const bandHi = inFc && model.hi ? Number(model.hi[hoverI - model.f0]) : null;

  return (
    <Box ref={containerRef} sx={{ width: '100%', position: 'relative' }}>
      <ChartTools
        zoomed={zoomed}
        onReset={reset}
        onZoomIn={() => zoomBy(0.6, 0.5)}
        onZoomOut={() => zoomBy(1.6, 0.5)}
      />
      <svg
        ref={svgRef} width={W} height={height}
        style={{ display: 'block', cursor: drag ? 'ew-resize' : 'crosshair', userSelect: 'none' }}
        onMouseMove={onMove} onMouseDown={onDown} onMouseUp={onUp}
        onMouseLeave={() => { setHoverI(null); setDrag(null); }}
        onDoubleClick={reset}
      >
        <defs>
          <linearGradient id="fanFcFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ACCENT} stopOpacity={0.28} />
            <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
          </linearGradient>
        </defs>
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={M.left} x2={W - M.right} y1={y(v)} y2={y(v)} stroke="currentColor" opacity={0.08} />
            <text x={M.left - 8} y={y(v) + 4} textAnchor="end" fontSize={11} fill="currentColor" opacity={0.55}>
              {fmtChartNum(v)}
            </text>
          </g>
        ))}
        {xTickIdx.map((i) => (model.times[i] ? (
          <text
            key={`xt-${i}`} x={x(i)} y={height - 9}
            textAnchor={i === i0 ? 'start' : i === i1 ? 'end' : 'middle'}
            fontSize={10} fill="currentColor" opacity={0.45}
          >
            {fmtChartTs(model.times[i])}
          </text>
        ) : null))}
        {nowVisible && (
          <>
            <line x1={x(lastHistIdx)} x2={x(lastHistIdx)} y1={M.top} y2={height - M.bottom} stroke="currentColor" opacity={0.3} strokeDasharray="4 4" />
            <text x={x(lastHistIdx)} y={M.top + 2} textAnchor="middle" fontSize={10} fill="currentColor" opacity={0.55}>{dividerLabel}</text>
          </>
        )}
        {bandPath && <path d={bandPath} fill={ACCENT} opacity={0.16} />}
        {fcArea && <path d={fcArea} fill="url(#fanFcFill)" />}
        {histPath && <path d={histPath} fill="none" stroke="currentColor" strokeWidth={1.5} opacity={0.7} />}
        {fcPath && <path d={fcPath} fill="none" stroke={ACCENT} strokeWidth={2.25} />}
        {eventMarks.map((m) => {
          if (m.idx < i0 || m.idx > i1) return null;
          const mx = x(m.idx);
          const my = height - M.bottom - 8 - (Math.min(m.stack || 0, 3) * 9);
          const imp = Number.isFinite(Number(m.importance)) ? Number(m.importance) : 0.5;
          const key = m.event_id || `${m.stream}-${m.occurred_at}`;
          return (
            <g
              key={key}
              onMouseEnter={() => setHoverEvent(m)}
              onMouseLeave={() => setHoverEvent(null)}
              style={{ cursor: 'pointer' }}
            >
              {m.scheduled && (
                <line
                  x1={mx} x2={mx} y1={M.top} y2={height - M.bottom}
                  stroke={EVENT_COLOR} opacity={0.22} strokeDasharray="2 4"
                />
              )}
              {/* generous invisible hit area so tiny dots are hoverable */}
              <circle cx={mx} cy={my} r={8} fill="transparent" />
              <circle
                cx={mx} cy={my} r={m.scheduled ? 4 : 3.5}
                fill={m.scheduled ? theme.palette.background.paper : EVENT_COLOR}
                stroke={EVENT_COLOR}
                strokeWidth={m.scheduled ? 1.75 : 1}
                opacity={0.45 + 0.55 * Math.max(0, Math.min(imp, 1))}
              />
            </g>
          );
        })}
        {hoverI != null && hv != null && !drag && !hoverEvent && (
          <g pointerEvents="none">
            <line x1={x(hoverI)} x2={x(hoverI)} y1={M.top} y2={height - M.bottom} stroke="currentColor" opacity={0.3} />
            <circle cx={x(hoverI)} cy={y(hv)} r={4.5} fill={inFc ? ACCENT : 'currentColor'} stroke="#fff" strokeWidth={1.5} />
          </g>
        )}
        {drag && (
          <rect
            x={Math.min(drag.a, drag.b)} y={M.top}
            width={Math.abs(drag.b - drag.a)} height={innerH}
            fill={ACCENT} opacity={0.12} stroke={ACCENT} strokeOpacity={0.4}
          />
        )}
      </svg>
      {hoverI != null && hv != null && !drag && !hoverEvent && (
        <ChartTooltip x={x(hoverI)} y={y(hv)} boxW={W}>
          <Typography variant="caption" color="text.secondary" display="block">
            {inFc ? 'Forecast' : 'History'} · {fmtChartTsFull(model.times[hoverI])}
          </Typography>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>{fmtVal(hv)}</Typography>
          {bandLo != null && bandHi != null && (
            <Typography variant="caption" sx={{ color: ACCENT }} display="block">
              q{model.qLabels[0]}–q{model.qLabels[1]}: {fmtChartNum(bandLo)} – {fmtVal(bandHi)}
            </Typography>
          )}
        </ChartTooltip>
      )}
      {hoverEvent && (
        <ChartTooltip
          x={x(Math.max(i0, Math.min(hoverEvent.idx, i1)))}
          y={height - M.bottom - 24}
          boxW={W}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            {hoverEvent.scheduled ? 'Scheduled event' : 'Event'} · {(() => {
              try { return fmtChartTsFull(new Date(Number(hoverEvent.occurred_at)).toISOString()); } catch (_e) { return '—'; }
            })()}
          </Typography>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
            {hoverEvent.name || hoverEvent.stream}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {hoverEvent.stream}
            {Number.isFinite(Number(hoverEvent.importance))
              ? ` · importance ${Math.round(Number(hoverEvent.importance) * 100)}%` : ''}
            {hoverEvent.edged ? ' · drives this goal' : ''}
          </Typography>
        </ChartTooltip>
      )}
      <Stack direction="row" spacing={2} sx={{ mt: 0.5, ml: 1 }} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          — history{unit ? ` (${unit})` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: ACCENT }}>— point forecast</Typography>
        {model.qLabels && (
          <Typography variant="caption" sx={{ color: ACCENT, opacity: 0.7 }}>
            ▮ q{model.qLabels[0]}–q{model.qLabels[1]} band
          </Typography>
        )}
        {eventMarks.length > 0 && (
          <Typography variant="caption" sx={{ color: EVENT_COLOR }}>
            ● events{eventMarks.some((m) => m.scheduled) ? ' · ○ scheduled' : ''}
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled">· drag or scroll to zoom</Typography>
      </Stack>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* ScoreBars                                                           */
/* ------------------------------------------------------------------ */

export const ScoreBars = ({ forecast, anomaly, height = 240 }) => {
  const theme = useTheme();
  const ACCENT = accentFor(theme.palette.mode);
  const [containerRef, width] = useChartWidth();
  const svgRef = useRef(null);
  const stateRef = useRef({});
  const [hoverI, setHoverI] = useState(null);
  const [drag, setDrag] = useState(null);

  const pts = useMemo(() => (forecast?.point || []).map(
    (v) => Math.max(0, Math.min(1, Number(v) || 0)),
  ), [forecast]);

  const n = pts.length;
  const {
    i0, i1, zoomed, reset, zoomTo, zoomBy,
  } = useIndexZoom(n);
  useWheelZoom(svgRef, stateRef);

  const M = { top: 20, right: 14, bottom: 30, left: 46 };
  const W = Math.max(width, 320);
  const innerW = W - M.left - M.right;
  const innerH = height - M.top - M.bottom;
  stateRef.current = { left: M.left, innerW, zoomBy };

  if (!n) return null;

  const visN = i1 - i0 + 1;
  const slot = innerW / visN;
  const bw = Math.min(slot * 0.62, 44);
  const y = (v) => M.top + innerH * (1 - v);
  const barColor = (v) => (v >= 0.7 ? '#e5484d' : v >= 0.4 ? '#f5a524' : '#30a46c');
  const yTicks = [0, 0.25, 0.5, 0.75, 1];
  const cx = (i) => M.left + slot * (i - i0) + slot / 2;
  const pxToIndex = (px) => Math.max(i0, Math.min(i1, i0 + Math.floor((px - M.left) / Math.max(slot, 1))));

  const showAllValues = visN <= 16;
  const labelEvery = Math.max(1, Math.ceil(visN / 8));

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    if (drag) { setDrag({ a: drag.a, b: px }); setHoverI(null); return; }
    const i = i0 + Math.floor((px - M.left) / Math.max(slot, 1));
    setHoverI(i >= i0 && i <= i1 ? i : null);
  };
  const onDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDrag({ a: e.clientX - rect.left, b: e.clientX - rect.left });
  };
  const onUp = () => {
    if (drag && Math.abs(drag.b - drag.a) > 6) zoomTo(pxToIndex(drag.a), pxToIndex(drag.b));
    setDrag(null);
  };

  const hv = hoverI != null ? pts[hoverI] : null;
  const hvLabel = (v) => {
    const pct = v * 100;
    if (anomaly) return pct >= 70 ? 'High anomaly risk' : pct >= 40 ? 'Elevated' : 'Normal';
    return pct >= 70 ? 'Strong upward' : pct >= 40 ? 'Mild upward' : 'No upward signal';
  };

  const visible = [];
  for (let i = i0; i <= i1; i += 1) visible.push(i);

  return (
    <Box ref={containerRef} sx={{ width: '100%', position: 'relative' }}>
      <ChartTools
        zoomed={zoomed}
        onReset={reset}
        onZoomIn={() => zoomBy(0.6, 0.5)}
        onZoomOut={() => zoomBy(1.6, 0.5)}
      />
      <svg
        ref={svgRef} width={W} height={height}
        style={{ display: 'block', cursor: drag ? 'ew-resize' : 'crosshair', userSelect: 'none' }}
        onMouseMove={onMove} onMouseDown={onDown} onMouseUp={onUp}
        onMouseLeave={() => { setHoverI(null); setDrag(null); }}
        onDoubleClick={reset}
      >
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={M.left} x2={W - M.right} y1={y(t)} y2={y(t)} stroke="currentColor" opacity={0.08} />
            <text x={M.left - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="currentColor" opacity={0.55}>
              {Math.round(t * 100)}%
            </text>
          </g>
        ))}
        <line x1={M.left} x2={W - M.right} y1={y(0.5)} y2={y(0.5)} stroke="#f5a524" opacity={0.5} strokeDasharray="5 4" />
        <text x={W - M.right} y={y(0.5) - 4} textAnchor="end" fontSize={10} fill="#f5a524" opacity={0.85}>
          {anomaly ? 'even odds' : 'coin flip (50%)'}
        </text>
        {visible.map((i) => {
          const v = pts[i];
          const top = y(v);
          const active = hoverI === i;
          return (
            <g key={i}>
              <rect
                x={cx(i) - bw / 2} y={top} width={bw}
                height={Math.max(0, y(0) - top)} fill={barColor(v)}
                opacity={active ? 1 : 0.82} rx={3}
                stroke={active ? 'currentColor' : 'none'} strokeWidth={active ? 1 : 0}
              />
              {(showAllValues || active) && (
                <text x={cx(i)} y={top - 5} textAnchor="middle" fontSize={11} fill="currentColor" opacity={active ? 1 : 0.75} fontWeight={active ? 700 : 400}>
                  {(v * 100).toFixed(0)}%
                </text>
              )}
              {(i - i0) % labelEvery === 0 && (
                <text x={cx(i)} y={height - 10} textAnchor="middle" fontSize={10} fill="currentColor" opacity={0.5}>
                  {forecast.step_at?.[i] ? fmtChartTs(forecast.step_at[i]) : `+${i + 1}`}
                </text>
              )}
            </g>
          );
        })}
        {drag && (
          <rect
            x={Math.min(drag.a, drag.b)} y={M.top}
            width={Math.abs(drag.b - drag.a)} height={innerH}
            fill={ACCENT} opacity={0.12} stroke={ACCENT} strokeOpacity={0.4}
          />
        )}
      </svg>
      {hoverI != null && hv != null && !drag && (
        <ChartTooltip x={cx(hoverI)} y={y(hv)} boxW={W}>
          <Typography variant="caption" color="text.secondary" display="block">
            Step +{hoverI + 1}{forecast.step_at?.[hoverI] ? ` · ${fmtChartTsFull(forecast.step_at[hoverI])}` : ''}
          </Typography>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.3, color: barColor(hv) }}>
            {(hv * 100).toFixed(0)}% · {hvLabel(hv)}
          </Typography>
        </ChartTooltip>
      )}
      <Stack direction="row" spacing={2} sx={{ mt: 0.5, ml: 1 }} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          {anomaly ? 'Anomaly likelihood per step — 0% means no anomaly predicted' : 'Upward-move likelihood per step'}
        </Typography>
        <Typography variant="caption" color="text.disabled">· drag or scroll to zoom</Typography>
      </Stack>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* ForecastValuesTable                                                 */
/* ------------------------------------------------------------------ */

export const ForecastValuesTable = ({
  forecast, mode = 'fan', anomaly = false, onExportCsv, maxHeight = 280,
}) => {
  const point = forecast?.point || [];
  if (!point.length) return null;
  const isScore = mode === 'score';
  const qs = Object.keys(forecast.quantiles || {}).sort((a, b) => Number(a) - Number(b));
  const lo = qs.length >= 2 ? forecast.quantiles[qs[0]] : null;
  const hi = qs.length >= 2 ? forecast.quantiles[qs[qs.length - 1]] : null;

  const signal = (v) => {
    const pct = v * 100;
    if (anomaly) return pct >= 70 ? 'High risk' : pct >= 40 ? 'Elevated' : 'Normal';
    return pct >= 70 ? 'Strong upward' : pct >= 40 ? 'Mild upward' : 'No signal';
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.4 }}>
          VALUES
        </Typography>
        {onExportCsv && (
          <Button size="small" startIcon={<FileDownloadIcon />} onClick={onExportCsv}
            sx={{ textTransform: 'none', fontSize: 11, py: 0.1 }}>
            CSV
          </Button>
        )}
      </Stack>
      <Box sx={{ maxHeight, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Step</TableCell>
              <TableCell>Time</TableCell>
              {isScore ? (
                <>
                  <TableCell align="right">Likelihood</TableCell>
                  <TableCell>Signal</TableCell>
                </>
              ) : (
                <>
                  <TableCell align="right">
                    Forecast{forecast.unit ? ` (${forecast.unit})` : ''}
                  </TableCell>
                  {lo && hi && <TableCell align="right">q{qs[0]}</TableCell>}
                  {lo && hi && <TableCell align="right">q{qs[qs.length - 1]}</TableCell>}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {point.map((v, k) => {
              const val = Number(v);
              return (
                <TableRow key={k} hover>
                  <TableCell>+{k + 1}</TableCell>
                  <TableCell>{forecast.step_at?.[k] ? fmtChartTsFull(forecast.step_at[k]) : '—'}</TableCell>
                  {isScore ? (
                    <>
                      <TableCell align="right">{(Math.max(0, Math.min(1, val)) * 100).toFixed(1)}%</TableCell>
                      <TableCell>{signal(Math.max(0, Math.min(1, val)))}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell align="right">{fmtChartNum(val)}</TableCell>
                      {lo && hi && <TableCell align="right">{fmtChartNum(Number(lo[k]))}</TableCell>}
                      {lo && hi && <TableCell align="right">{fmtChartNum(Number(hi[k]))}</TableCell>}
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Adapter: inference-page per-label prediction -> shared forecast     */
/* ------------------------------------------------------------------ */

// The inference page carries `prediction = {label, point, quantiles}` plus a
// per-label `history = [{x, y}]` and a flat `intervalSec` + `inferenceTs`,
// whereas the shared charts want a single `forecast` object with explicit
// per-step timestamps. This bridges the two so both pages render identically.
export const predictionToForecast = ({
  prediction, history, intervalSec, inferenceTs, servingMode, unit,
}) => {
  const flat = (a) => {
    if (a == null) return [];
    if (!Array.isArray(a)) return [Number(a)];
    return a.flat(Infinity).map((v) => Number(v));
  };
  const point = flat(prediction?.point);
  const quantiles = {};
  Object.entries(prediction?.quantiles || {}).forEach(([q, vals]) => {
    quantiles[q] = flat(vals);
  });
  const hv = (history || []).map((h) => (h?.y == null ? null : Number(h.y)));
  const hAt = (history || []).map((h) => {
    const t = h?.x;
    if (t == null) return null;
    const d = typeof t === 'number' ? new Date(t) : new Date(t);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  });
  const stepMs = (intervalSec || 60) * 1000;
  // Anchor the forecast on the last realized history point — NOT wall clock.
  // A historical series (e.g. ending 1960) served today must extend its steps
  // from that last point, not from "now". Fall back to the inference time only
  // when there is no history to anchor on.
  const lastHistMs = (() => {
    for (let i = hAt.length - 1; i >= 0; i -= 1) {
      if (hAt[i]) return new Date(hAt[i]).getTime();
    }
    return null;
  })();
  const baseMs = lastHistMs != null
    ? lastHistMs
    : (inferenceTs ? new Date(inferenceTs).getTime() : Date.now());
  const step_at = point.map((_v, k) => new Date(baseMs + (k + 1) * stepMs).toISOString());
  return {
    point,
    quantiles,
    history: { values: hv, step_at: hAt },
    step_at,
    at: inferenceTs,
    serving_mode: servingMode,
    unit: unit || null,
  };
};
